import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  Switch,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Badge,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Visibility,
  Search,
  FilterList,
  GetApp,
  Security,
  Group,
  Person,
  AdminPanelSettings,
  SupervisorAccount,
  Engineering,
  Construction,
  Business,
  Assignment,
  CheckCircle,
  Cancel,
  Warning,
  Info,
  ExpandMore,
  Lock,
  LockOpen,
  Shield,
  Key,
  Visibility as VisibilityIcon,
  VisibilityOff,
  Settings,
  Dashboard,
  Assessment,
  Description,
  AttachMoney,
  PhotoCamera,
  BugReport,
  People,
  Notifications,
} from '@mui/icons-material';
import { roleManagementService, Role as ApiRole, Permission as ApiPermission } from '../services/roleManagementService';
import { API_BASE_URL } from '../services/config';

interface Permission {
  id: string;
  name: string;
  description: string;
  category: string;
  resource: string;
  actions: string[];
}

interface Role {
  id: string;
  name: string;
  description: string;
  type: 'system' | 'custom';
  level: 'admin' | 'manager' | 'user' | 'viewer';
  permissions: string[];
  userCount: number;
  isActive: boolean;
  createdDate: string;
  lastModified: string;
  createdBy: string;
}

interface User {
  id: string;
  name: string;
  email: string;
  avatar?: string;
  roleId: string;
  status: 'active' | 'inactive' | 'suspended';
}







const RoleManagement: React.FC = () => {
  const [roles, setRoles] = useState<Role[]>([]);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [levelFilter, setLevelFilter] = useState<string>('all');
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newRoleName, setNewRoleName] = useState('');
  const [newRoleDescription, setNewRoleDescription] = useState('');
  const [selectedPermissions, setSelectedPermissions] = useState<string[]>([]);
  const [totalUsers, setTotalUsers] = useState<number>(0);
  const [manageDialogOpen, setManageDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editRoleName, setEditRoleName] = useState('');
  const [editRoleDescription, setEditRoleDescription] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [rolesData, permissionsData] = await Promise.all([
          roleManagementService.getRoles(),
          roleManagementService.getPermissions()
        ]);
        
        // Fetch total user count from backend stats
        try {
  const baseURL = API_BASE_URL;
          const token = localStorage.getItem('token');
          const usersStatsRes = await axios.get(`${baseURL}/users/stats/overview`, {
            headers: token ? { Authorization: `Bearer ${token}` } : {}
          });
          const overview = usersStatsRes?.data?.overview;
          const computedTotal = overview?.totalUsers ?? usersStatsRes?.data?.total ?? 0;
          setTotalUsers(typeof computedTotal === 'number' ? computedTotal : 0);

          // Build derived roles from role distribution if no Role documents exist
          const roleDistribution = overview?.roleDistribution ?? usersStatsRes?.data?.roleDistribution ?? [];
          const toTitle = (key: string) => key
            .split('_')
            .map(s => s.charAt(0).toUpperCase() + s.slice(1))
            .join(' ');
          const toLevel = (key: string): 'admin' | 'manager' | 'user' | 'viewer' => {
            if (key === 'admin') return 'admin';
            if (['project_manager','supervisor'].includes(key)) return 'manager';
            if (['safety_officer','field_worker','contractor'].includes(key)) return 'user';
            return 'user';
          };
          const derivedRoles: Role[] = Array.isArray(roleDistribution)
            ? roleDistribution.map((item: any) => ({
                id: String(item.role || item._id || item.name || 'unknown'),
                name: toTitle(String(item.role || item._id || item.name || 'unknown')),
                description: 'System role derived from user records',
                type: 'system',
                level: toLevel(String(item.role || item._id || item.name || 'unknown')),
                permissions: [],
                userCount: Number(item.count || item.total || 0),
                isActive: true,
                createdDate: new Date().toISOString(),
                lastModified: new Date().toISOString(),
                createdBy: 'System',
              }))
            : [];
          // If backend returns no Role documents, fallback to derived roles
          if (Array.isArray(rolesData) && rolesData.length === 0 && derivedRoles.length > 0) {
            setRoles(derivedRoles);
          }
        } catch (statsErr) {
          console.warn('Could not fetch users stats for Role Management:', statsErr);
          setTotalUsers(0);
        }
        
        // Transform API data to match local interfaces
        const transformedRoles = rolesData.map(role => ({
          id: role._id,
          name: role.name,
          description: role.description,
          type: (role.category === 'system' ? 'system' : 'custom') as 'system' | 'custom',
          level: (role.level === 1 ? 'admin' : role.level === 2 ? 'manager' : role.level === 3 ? 'user' : 'viewer') as 'admin' | 'manager' | 'user' | 'viewer',
          permissions: role.permissions.map(p => p._id),
          userCount: role.users.length,
          isActive: role.isActive,
          createdDate: role.createdAt,
          lastModified: role.updatedAt,
          createdBy: role.createdBy.name
        }));
        
        const transformedPermissions = permissionsData.map(permission => ({
          id: permission._id,
          name: permission.displayName,
          description: permission.description,
          category: permission.category,
          resource: permission.resource,
          actions: permission.actions.map(a => a.name)
        }));
        
        // Only set transformed roles if we didn't already set derived roles fallback
        if (!(Array.isArray(rolesData) && rolesData.length === 0)) {
          setRoles(transformedRoles);
        }
        setPermissions(transformedPermissions);
        // No mock users; leave empty until endpoint available
        setUsers([]);
      } catch (error) {
        console.error('Error fetching role management data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Ensure arrays are always arrays for safe operations
  const rolesArray = Array.isArray(roles) ? roles : [];
  const permissionsArray = Array.isArray(permissions) ? permissions : [];
  const usersArray = Array.isArray(users) ? users : [];

  const filteredRoles = rolesArray.filter((role) => {
    const matchesSearch = role.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         role.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = typeFilter === 'all' || role.type === typeFilter;
    const matchesLevel = levelFilter === 'all' || role.level === levelFilter;
    return matchesSearch && matchesType && matchesLevel;
  });

  const totalRoles = rolesArray.length;
  const activeRoles = rolesArray.filter(r => r.isActive).length;
  const customRoles = rolesArray.filter(r => r.type === 'custom').length;

  const getLevelIcon = (level: string) => {
    switch (level) {
      case 'admin': return <AdminPanelSettings />;
      case 'manager': return <SupervisorAccount />;
      case 'user': return <Person />;
      case 'viewer': return <VisibilityIcon />;
      default: return <Person />;
    }
  };

  const getLevelColor = (level: string) => {
    switch (level) {
      case 'admin': return 'error';
      case 'manager': return 'warning';
      case 'user': return 'primary';
      case 'viewer': return 'info';
      default: return 'default';
    }
  };

  const getTypeColor = (type: string) => {
    return type === 'system' ? 'primary' : 'secondary';
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const handleViewRole = (role: Role) => {
    setSelectedRole(role);
    setViewDialogOpen(true);
  };

  const handleManagePermissions = (role: Role) => {
    // Guard: derived system roles are fallback-only and have no backend document
    if (isDerivedSystemRole(role)) return;
    setSelectedRole(role);
    // Preload current permissions for the selected role
    setSelectedPermissions(Array.isArray(role.permissions) ? role.permissions : []);
    setManageDialogOpen(true);
  };

  const handleEditRoleOpen = (role: Role) => {
    setSelectedRole(role);
    setEditRoleName(role.name);
    setEditRoleDescription(role.description);
    setEditDialogOpen(true);
  };

  const isDerivedSystemRole = (role: Role) => role.createdBy === 'System' && role.type === 'system' && role.permissions.length === 0;

  const savePermissions = async () => {
    if (!selectedRole) return;
    // Prevent saving for derived roles (no backend document) or invalid ids
    if (isDerivedSystemRole(selectedRole)) {
      setManageDialogOpen(false);
      return;
    }
    const isObjectId = /^[a-f\d]{24}$/i.test(selectedRole.id);
    if (!isObjectId) {
      console.warn('Selected role has no persistent backend id; aborting permission save.');
      setManageDialogOpen(false);
      return;
    }
    try {
  const baseURL = API_BASE_URL;
      const token = localStorage.getItem('token');
      await axios.put(`${baseURL}/roles/${selectedRole.id}/permissions`, { permissions: selectedPermissions }, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setRoles(prev => prev.map(r => r.id === selectedRole.id ? { ...r, permissions: selectedPermissions } : r));
      setManageDialogOpen(false);
    } catch (err) {
      console.error('Failed to update role permissions:', err);
    }
  };

  const saveRoleEdits = async () => {
    if (!selectedRole) return;
    try {
  const baseURL = API_BASE_URL;
      const token = localStorage.getItem('token');
      const payload: any = {};
      if (editRoleName && editRoleName !== selectedRole.name) payload.name = editRoleName;
      if (editRoleDescription && editRoleDescription !== selectedRole.description) payload.description = editRoleDescription;
      if (Object.keys(payload).length === 0) {
        setEditDialogOpen(false);
        return;
      }
      const res = await axios.put(`${baseURL}/roles/${selectedRole.id}`, payload, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      const updated = res?.data?.data;
      setRoles(prev => prev.map(r => r.id === selectedRole.id ? {
        ...r,
        name: updated?.name ?? editRoleName,
        description: updated?.description ?? editRoleDescription,
        lastModified: updated?.updatedAt ?? new Date().toISOString(),
        createdBy: r.createdBy
      } : r));
      setEditDialogOpen(false);
    } catch (err) {
      console.error('Failed to update role:', err);
    }
  };

  const toggleRoleActive = async (role: Role) => {
    if (isDerivedSystemRole(role)) return;
    try {
  const baseURL = API_BASE_URL;
      const token = localStorage.getItem('token');
      await axios.put(`${baseURL}/roles/${role.id}`, { isActive: !role.isActive }, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setRoles(prev => prev.map(r => r.id === role.id ? { ...r, isActive: !role.isActive } : r));
    } catch (err) {
      console.error('Failed to toggle role active state:', err);
    }
  };

  const deleteRole = async (role: Role) => {
    try {
  const baseURL = API_BASE_URL;
      const token = localStorage.getItem('token');
      await axios.delete(`${baseURL}/roles/${role.id}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setRoles(prev => prev.filter(r => r.id !== role.id));
    } catch (err) {
      console.error('Failed to delete role:', err);
    }
  };

  const handleCreateRole = () => {
    setNewRoleName('');
    setNewRoleDescription('');
    setSelectedPermissions([]);
    setCreateDialogOpen(true);
  };

  const handlePermissionToggle = (permissionId: string) => {
    setSelectedPermissions(prev => 
      prev.includes(permissionId)
        ? prev.filter(id => id !== permissionId)
        : [...prev, permissionId]
    );
  };

  const groupedPermissions = permissionsArray.reduce((groups, permission) => {
    const category = permission?.category || 'Other';
    if (!groups[category]) {
      groups[category] = [];
    }
    groups[category].push(permission);
    return groups;
  }, {} as Record<string, Permission[]>);

  const getRoleUsers = (roleId: string) => {
    return usersArray.filter(user => user.roleId === roleId);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Role Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleCreateRole}
          sx={{ bgcolor: 'primary.main' }}
        >
          Create Role
        </Button>
      </Box>

      {/* Summary Cards */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 4 }}>
        <Card sx={{ minWidth: 200, flex: 1 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Security color="primary" sx={{ fontSize: 40 }} />
              <Box>
                <Typography color="textSecondary" gutterBottom>
                  Total Roles
                </Typography>
                <Typography variant="h5">
                  {totalRoles}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
        <Card sx={{ minWidth: 200, flex: 1 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <CheckCircle color="success" sx={{ fontSize: 40 }} />
              <Box>
                <Typography color="textSecondary" gutterBottom>
                  Active Roles
                </Typography>
                <Typography variant="h5">
                  {activeRoles}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
        <Card sx={{ minWidth: 200, flex: 1 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Settings color="info" sx={{ fontSize: 40 }} />
              <Box>
                <Typography color="textSecondary" gutterBottom>
                  Custom Roles
                </Typography>
                <Typography variant="h5">
                  {customRoles}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
        <Card sx={{ minWidth: 200, flex: 1 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Group color="warning" sx={{ fontSize: 40 }} />
              <Box>
                <Typography color="textSecondary" gutterBottom>
                  Total Users
                </Typography>
                <Typography variant="h5">
                  {totalUsers}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Filters and Search */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            placeholder="Search roles..."
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: <Search sx={{ color: 'text.secondary', mr: 1 }} />,
            }}
            sx={{ minWidth: 250 }}
          />
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Type</InputLabel>
            <Select
              value={typeFilter}
              label="Type"
              onChange={(e) => setTypeFilter(e.target.value)}
            >
              <MenuItem value="all">All Types</MenuItem>
              <MenuItem value="system">System</MenuItem>
              <MenuItem value="custom">Custom</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Level</InputLabel>
            <Select
              value={levelFilter}
              label="Level"
              onChange={(e) => setLevelFilter(e.target.value)}
            >
              <MenuItem value="all">All Levels</MenuItem>
              <MenuItem value="admin">Admin</MenuItem>
              <MenuItem value="manager">Manager</MenuItem>
              <MenuItem value="user">User</MenuItem>
              <MenuItem value="viewer">Viewer</MenuItem>
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            startIcon={<FilterList />}
            sx={{ ml: 'auto' }}
          >
            More Filters
          </Button>
          <Button
            variant="outlined"
            startIcon={<GetApp />}
          >
            Export
          </Button>
        </Box>
      </Paper>

      {/* Roles Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Role</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Level</TableCell>
                <TableCell>Permissions</TableCell>
                <TableCell>Users</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Last Modified</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRoles.map((role) => {
                const roleUsers = getRoleUsers(role.id);
                
                return (
                  <TableRow key={role.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar sx={{ bgcolor: getLevelColor(role.level) + '.main' }}>
                          {getLevelIcon(role.level)}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {role.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {role.description}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={role.type}
                        color={getTypeColor(role.type) as any}
                        size="small"
                        sx={{ textTransform: 'capitalize' }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={role.level}
                        color={getLevelColor(role.level) as any}
                        size="small"
                        sx={{ textTransform: 'capitalize' }}
                      />
                    </TableCell>
                    <TableCell>
                      <Tooltip title={isDerivedSystemRole(role) ? 'Derived role: manage disabled' : 'Manage permissions'}>
                        <span>
                          <Button
                            variant="outlined"
                            size="small"
                            disabled={isDerivedSystemRole(role)}
                            onClick={() => handleManagePermissions(role)}
                          >
                            Manage ({role.permissions.length})
                          </Button>
                        </span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" fontWeight="medium">
                          {role.userCount}
                        </Typography>
                        {roleUsers.length > 0 && (
                          <Box sx={{ display: 'flex', ml: 1 }}>
                            {roleUsers.slice(0, 3).map((user, index) => (
                              <Avatar
                                key={user.id}
                                sx={{
                                  width: 24,
                                  height: 24,
                                  fontSize: '0.75rem',
                                  ml: index > 0 ? -0.5 : 0,
                                  border: '2px solid white',
                                }}
                              >
                                {user.name.split(' ').map(n => n[0]).join('')}
                              </Avatar>
                            ))}
                            {roleUsers.length > 3 && (
                              <Avatar
                                sx={{
                                  width: 24,
                                  height: 24,
                                  fontSize: '0.75rem',
                                  ml: -0.5,
                                  bgcolor: 'grey.400',
                                  border: '2px solid white',
                                }}
                              >
                                +{roleUsers.length - 3}
                              </Avatar>
                            )}
                          </Box>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {role.isActive ? (
                          <CheckCircle color="success" sx={{ fontSize: 16 }} />
                        ) : (
                          <Cancel color="error" sx={{ fontSize: 16 }} />
                        )}
                        <Typography variant="body2">
                          {role.isActive ? 'Active' : 'Inactive'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(role.lastModified)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        by {role.createdBy}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          onClick={() => handleViewRole(role)}
                        >
                          <Visibility />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <span>
                          <IconButton size="small" disabled={isDerivedSystemRole(role)} onClick={() => handleEditRoleOpen(role)}>
                            <Edit />
                          </IconButton>
                        </span>
                      </Tooltip>
                      {role.type === 'custom' && (
                        <Tooltip title="Delete">
                          <IconButton size="small" color="error" onClick={() => deleteRole(role)}>
                            <Delete />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title={role.isActive ? 'Deactivate' : 'Activate'}>
                        <IconButton size="small" color="primary" disabled={isDerivedSystemRole(role)} onClick={() => toggleRoleActive(role)}>
                          {role.isActive ? <Lock /> : <LockOpen />}
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* View Role Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Role Details - {selectedRole?.name}
        </DialogTitle>
        <DialogContent>
          {selectedRole && (
            <Box sx={{ pt: 2 }}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 3 }}>
                <Box sx={{ flex: 1, minWidth: 200 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Description
                  </Typography>
                  <Typography variant="body1">
                    {selectedRole.description}
                  </Typography>
                </Box>
                <Box sx={{ flex: 1, minWidth: 200 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Type
                  </Typography>
                  <Chip
                    label={selectedRole.type}
                    color={getTypeColor(selectedRole.type) as any}
                    size="small"
                    sx={{ textTransform: 'capitalize' }}
                  />
                </Box>
                <Box sx={{ flex: 1, minWidth: 200 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Level
                  </Typography>
                  <Chip
                    label={selectedRole.level}
                    color={getLevelColor(selectedRole.level) as any}
                    size="small"
                    sx={{ textTransform: 'capitalize' }}
                  />
                </Box>
              </Box>
              
              <Divider sx={{ my: 3 }} />
              
              <Typography variant="h6" gutterBottom>
                Permissions ({selectedRole.permissions.length})
              </Typography>
              
              {Object.entries(groupedPermissions).map(([category, categoryPermissions]) => {
                const categoryPermissionIds = categoryPermissions.map(p => p.id);
                const hasPermissions = categoryPermissionIds.some(id => selectedRole.permissions.includes(id));
                
                if (!hasPermissions) return null;
                
                return (
                  <Accordion key={category} defaultExpanded>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                      <Typography variant="subtitle1" fontWeight="medium">
                        {category}
                      </Typography>
                      <Chip
                        label={categoryPermissionIds.filter(id => selectedRole.permissions.includes(id)).length}
                        size="small"
                        sx={{ ml: 2 }}
                      />
                    </AccordionSummary>
                    <AccordionDetails>
                      <List dense>
                        {categoryPermissions
                          .filter(permission => selectedRole.permissions.includes(permission.id))
                          .map((permission) => (
                            <ListItem key={permission.id}>
                              <ListItemIcon>
                                <CheckCircle color="success" sx={{ fontSize: 20 }} />
                              </ListItemIcon>
                              <ListItemText
                                primary={permission.name}
                                secondary={permission.description}
                              />
                            </ListItem>
                          ))}
                      </List>
                    </AccordionDetails>
                  </Accordion>
                );
              })}
              
              <Divider sx={{ my: 3 }} />
              
              <Typography variant="h6" gutterBottom>
                Assigned Users ({selectedRole.userCount})
              </Typography>
              
              {getRoleUsers(selectedRole.id).length > 0 ? (
                <List>
                  {getRoleUsers(selectedRole.id).map((user) => (
                    <ListItem key={user.id}>
                      <ListItemIcon>
                        <Avatar sx={{ width: 32, height: 32 }}>
                          {user.name.split(' ').map(n => n[0]).join('')}
                        </Avatar>
                      </ListItemIcon>
                      <ListItemText
                        primary={user.name}
                        secondary={user.email}
                      />
                      <ListItemSecondaryAction>
                        <Chip
                          label={user.status}
                          color={user.status === 'active' ? 'success' : 'default'}
                          size="small"
                        />
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Alert severity="info">
                  No users are currently assigned to this role.
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
          <Button startIcon={<People />}>
            Assign Users
          </Button>
          <Button variant="contained" startIcon={<Edit />}>
            Edit Role
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Role Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Create New Role
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Role Name"
              value={newRoleName}
              onChange={(e) => setNewRoleName(e.target.value)}
              sx={{ mb: 3 }}
            />
            <TextField
              fullWidth
              label="Description"
              multiline
              rows={3}
              value={newRoleDescription}
              onChange={(e) => setNewRoleDescription(e.target.value)}
              sx={{ mb: 3 }}
            />
            
            <Typography variant="h6" gutterBottom>
              Permissions
            </Typography>
            
            {Object.entries(groupedPermissions).map(([category, categoryPermissions]) => (
              <Accordion key={category}>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant="subtitle1" fontWeight="medium">
                    {category}
                  </Typography>
                  <Chip
                    label={categoryPermissions.filter(p => selectedPermissions.includes(p.id)).length}
                    size="small"
                    sx={{ ml: 2 }}
                  />
                </AccordionSummary>
                <AccordionDetails>
                  <FormGroup>
                    {categoryPermissions.map((permission) => (
                      <FormControlLabel
                        key={permission.id}
                        control={
                          <Checkbox
                            checked={selectedPermissions.includes(permission.id)}
                            onChange={() => handlePermissionToggle(permission.id)}
                          />
                        }
                        label={
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              {permission.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {permission.description}
                            </Typography>
                          </Box>
                        }
                      />
                    ))}
                  </FormGroup>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!newRoleName.trim() || selectedPermissions.length === 0}
          >
            Create Role
          </Button>
        </DialogActions>
      </Dialog>

      {/* Manage Permissions Dialog */}
      <Dialog
        open={manageDialogOpen}
        onClose={() => setManageDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Manage Permissions - {selectedRole?.name}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {Object.entries(groupedPermissions).map(([category, categoryPermissions]) => (
              <Accordion key={category}>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant="subtitle1" fontWeight="medium">
                    {category}
                  </Typography>
                  <Chip
                    label={categoryPermissions.filter(p => selectedPermissions.includes(p.id)).length}
                    size="small"
                    sx={{ ml: 2 }}
                  />
                </AccordionSummary>
                <AccordionDetails>
                  <FormGroup>
                    {categoryPermissions.map((permission) => (
                      <FormControlLabel
                        key={permission.id}
                        control={
                          <Checkbox
                            checked={selectedPermissions.includes(permission.id)}
                            onChange={() => handlePermissionToggle(permission.id)}
                          />
                        }
                        label={
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              {permission.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {permission.description}
                            </Typography>
                          </Box>
                        }
                      />
                    ))}
                  </FormGroup>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setManageDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={savePermissions}
            disabled={!selectedRole || isDerivedSystemRole(selectedRole)}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Role Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Edit Role - {selectedRole?.name}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Role Name"
              value={editRoleName}
              onChange={(e) => setEditRoleName(e.target.value)}
              sx={{ mb: 3 }}
            />
            <TextField
              fullWidth
              label="Description"
              multiline
              rows={3}
              value={editRoleDescription}
              onChange={(e) => setEditRoleDescription(e.target.value)}
              sx={{ mb: 3 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveRoleEdits}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RoleManagement;